import type {
  CampaignCalendarConnectorSetupQuery,
  CampaignCalendarDestinationPermissionsQuery,
  CampaignCalendarEngagementSetupQuery,
} from "src/graphql";
import { CONNECTIONS_PATH } from "src/pages/context-hub/connections/paths";

type ConnectorSetup =
  CampaignCalendarConnectorSetupQuery["campaignCalendarConnectorSetup"][number];
type EngagementSetup =
  CampaignCalendarEngagementSetupQuery["campaignCalendarEngagementSetup"][number];
type DestinationPermission =
  CampaignCalendarDestinationPermissionsQuery["checkDestinationPermissions"][number];

/**
 * `importing` covers connecting, the first Fivetran sync and the first
 * calendar build, which share one overlay.
 */
export type CalendarSendsState =
  | "not_connected"
  | "importing"
  | "broken"
  | "ready";

/**
 * `checking` is a step check with no result yet; `check_failed` is a check that
 * errored, so the step's real state is unknown.
 */
export type CalendarSetupStepStatus =
  | "done"
  | "todo"
  | "in_progress"
  | "error"
  | "checking"
  | "check_failed";

export type CalendarSetup<
  TState extends CalendarSendsState = CalendarSendsState,
> = {
  sendsState: TState;
  sendsStep: CalendarSetupStepStatus;
  connectors: readonly ConnectorSetup[];
};

/** The latest result of a step's check query. */
export type CalendarStepCheck<TData> = {
  data: TData | undefined;
  isError: boolean;
};

/** A destination with no `missing` permissions already has access. */
export type CalendarPermissionsDestination = Pick<
  DestinationPermission,
  "destinationName" | "missing"
> & { destinationId: string };

export type CalendarPermissionsGroup = {
  provider: string;
  destinations: CalendarPermissionsDestination[];
};

export type CalendarPermissionsAction =
  | { kind: "update_permissions" }
  | { kind: "link_destination"; provider: string };

export type CalendarPermissionsStep = {
  /** `unavailable` when the user cannot read any destination the check covers. */
  status: CalendarSetupStepStatus | "unavailable";
  action: CalendarPermissionsAction | null;
  /** Every checked destination, so the step can list them whether or not one needs a change. */
  groups: CalendarPermissionsGroup[];
};

/** How one email provider connection reads in the setup panel. */
export type CalendarConnectorStatus = "connected" | "importing" | "broken";

export const CALENDAR_CONNECTIONS_PATH = CONNECTIONS_PATH;

/**
 * Any connector with published data makes the calendar readable, so a broken
 * connector only blocks the page when no other connector has data.
 */
export function deriveCalendarSendsState(
  connectors: readonly ConnectorSetup[],
): CalendarSendsState {
  if (connectors.length === 0) return "not_connected";
  if (connectors.some((connector) => connector.hasCalendarData)) return "ready";
  return connectors.every((connector) => connector.connectionState === "broken")
    ? "broken"
    : "importing";
}

export function deriveSendsStep(
  connectors: readonly ConnectorSetup[],
): CalendarSetupStepStatus {
  if (connectors.some(({ connectionState }) => connectionState === "broken")) {
    return "error";
  }
  const state = deriveCalendarSendsState(connectors);
  if (state === "ready") return "done";
  return state === "importing" ? "in_progress" : "todo";
}

/** A failed check outranks earlier data, so a failed "Check again" never looks like it passed. */
export function deriveEngagementStep({
  data,
  isError,
}: CalendarStepCheck<readonly EngagementSetup[]>): CalendarSetupStepStatus {
  if (isError) return "check_failed";
  if (!data) return "checking";
  return data.length > 0 && data.every(({ state }) => state !== "missing")
    ? "done"
    : "todo";
}

export function deriveDefinitionsStep({
  data,
  isError,
}: CalendarStepCheck<boolean>): CalendarSetupStepStatus {
  if (isError) return "check_failed";
  if (data === undefined) return "checking";
  return data ? "done" : "todo";
}

const UNLINKED_REASONS: ReadonlySet<string> = new Set([
  "no_destination_linked",
  "destination_not_supported",
]);
const CHECK_FAILED_REASONS: ReadonlySet<string> = new Set([
  "configuration_unavailable",
  "provider_check_failed",
]);

/** Asks for a change only when one would help: missing permissions first, then an unlinked connector. */
export function derivePermissionsStep({
  data,
  isError,
}: CalendarStepCheck<
  readonly DestinationPermission[]
>): CalendarPermissionsStep {
  if (isError) return { status: "check_failed", action: null, groups: [] };
  if (!data) return { status: "checking", action: null, groups: [] };
  // The check leaves out destinations the user cannot read.
  if (data.length === 0) {
    return { status: "unavailable", action: null, groups: [] };
  }
  const groups = groupPermissionsByProvider(data);
  if (
    groups.some(({ destinations }) =>
      destinations.some(({ missing }) => missing.length > 0),
    )
  ) {
    return { status: "todo", action: { kind: "update_permissions" }, groups };
  }
  const unlinked = data.find(
    ({ reason }) => reason !== null && UNLINKED_REASONS.has(reason),
  );
  if (unlinked) {
    return {
      status: "todo",
      action: { kind: "link_destination", provider: unlinked.provider },
      groups,
    };
  }
  if (
    data.some(
      ({ reason }) => reason !== null && CHECK_FAILED_REASONS.has(reason),
    )
  ) {
    return { status: "check_failed", action: null, groups };
  }
  // Unverified with no reason means an empty catalog or an inconclusive probe, which the user cannot fix.
  return { status: "done", action: null, groups };
}

/** One group per provider, listing every destination the check could verify, in the order the check returned them. */
export function groupPermissionsByProvider(
  data: readonly DestinationPermission[],
): CalendarPermissionsGroup[] {
  const groups = new Map<string, CalendarPermissionsGroup>();
  for (const {
    status,
    destinationId,
    destinationName,
    provider,
    missing,
  } of data) {
    if (
      destinationId === null ||
      (status !== "missing" && status !== "ready")
    ) {
      continue;
    }
    const group = groups.get(provider) ?? { provider, destinations: [] };
    groups.set(provider, group);
    group.destinations.push({
      destinationId,
      destinationName,
      missing: uniqueByPermission(missing),
    });
  }
  return [...groups.values()];
}

/** A broken connector reads as broken even with published data, since its sends stop updating. */
export function deriveConnectorStatus({
  connectionState,
  hasCalendarData,
}: Pick<
  ConnectorSetup,
  "connectionState" | "hasCalendarData"
>): CalendarConnectorStatus {
  if (connectionState === "broken") return "broken";
  return hasCalendarData ? "connected" : "importing";
}

/** A provider can list one scope for several purposes, but the user grants it once. */
function uniqueByPermission(
  missing: DestinationPermission["missing"],
): DestinationPermission["missing"] {
  return missing.filter(
    ({ permission }, index) =>
      missing.findIndex((m) => m.permission === permission) === index,
  );
}

/** Counts the steps the setup panel lists; the permissions step is left out when the user cannot read any destination. */
export function deriveSetupProgress({
  contextStep,
  definitionsStep,
  sendsStep,
  engagementStep,
  permissionsStep,
}: {
  definitionsStep?: CalendarSetupStepStatus;
  contextStep?: CalendarSetupStepStatus;
  sendsStep: CalendarSetupStepStatus;
  engagementStep: CalendarSetupStepStatus;
  permissionsStep: CalendarPermissionsStep["status"];
}): { done: number; total: number } {
  const steps = [
    sendsStep,
    engagementStep,
    ...(permissionsStep === "unavailable" ? [] : [permissionsStep]),
    ...(definitionsStep === undefined ? [] : [definitionsStep]),
    ...(contextStep === undefined ? [] : [contextStep]),
  ];
  return {
    done: steps.filter((status) => status === "done").length,
    total: steps.length,
  };
}

/** Whether the step's icon spins: work is underway or its check is running. */
export function isStepInProgress(
  status: CalendarPermissionsStep["status"],
  isFetching: boolean,
): boolean {
  return isFetching || status === "in_progress" || status === "checking";
}

export type CalendarSidebarPanel = "setup" | "agent" | "insights";

/** Setup remains available so users can manage Calendar connections and definitions. */
export function deriveCalendarSidebarPanels({
  setup,
  agentAvailable,
  showInsights = false,
}: {
  setup: Pick<CalendarSetup, "sendsState" | "sendsStep">;
  agentAvailable: boolean;
  /** Off while an editor view is open. */
  showInsights?: boolean;
}): {
  panels: CalendarSidebarPanel[];
  lockedPanel: CalendarSidebarPanel | undefined;
} {
  const isImporting = setup.sendsState !== "ready";
  // The agent has nothing to read until sends are imported.
  const showAgent = agentAvailable && !isImporting;
  return {
    panels: [
      "setup",
      ...(showAgent ? ["agent" as const] : []),
      // Insights wait with the agent while sends import.
      ...(showInsights && !isImporting ? ["insights" as const] : []),
    ],
    lockedPanel: isImporting ? "setup" : undefined,
  };
}
